// Server registration — POST bunker URL to always-on server, probe status, provision.
//
// Thin delegate over @synvya/auth-identity's shared server-registration
// module (auth-identity#25). account injects its own browser glue —
// `serverFetch` as `fetchJson` and `ensureMerchantSession` as
// `ensureSession` — and re-exports the shared functions behind account's
// existing signatures so call sites and tests stay unchanged.

import { serverFetch } from '@/lib/serverFetch'
import { ApiError } from '@/lib/apiError'
import { ensureMerchantSession } from '@/services/merchantAuth'
import {
  registerRestaurantWithServer as sharedRegister,
  fetchServerRestaurantStatus as sharedFetchStatus,
  provisionServer as sharedProvisionServer,
  systemTimezone,
} from '@synvya/auth-identity/server-registration'
import type {
  ServerRegistrationInput,
  ServerRestaurantSnapshot,
} from '@synvya/auth-identity/server-registration'
import { isValidTimeZone } from '@synvya/capabilities/service-dates'
import type { EventTemplate, VerifiedEvent } from 'nostr-tools'

// ── Types ────────────────────────────────────────────────────────────

export type { ServerRegistrationInput, ServerRestaurantSnapshot }

export type RestaurantSigner = (
  template: EventTemplate,
) => Promise<VerifiedEvent>

// ── Public API ───────────────────────────────────────────────────────

export async function registerRestaurantWithServer(
  input: ServerRegistrationInput,
): Promise<ServerRestaurantSnapshot> {
  return sharedRegister(serverFetch, input)
}

export async function fetchServerRestaurantStatus(
  restaurantPubkey: string,
): Promise<ServerRestaurantSnapshot | 'not_found'> {
  return sharedFetchStatus(serverFetch, restaurantPubkey)
}

/**
 * End-to-end "establish merchant session + register restaurant" with
 * a single silent retry on 401. Call after minting the Keycast
 * authorization — this is pure server HTTP composition.
 */
export async function provisionServer(args: {
  restaurantPubkey: string
  serverBunkerUrl: string
  authorizationLabel: string
  timezone: string
  signAsRestaurant: RestaurantSigner
}): Promise<ServerRestaurantSnapshot> {
  return sharedProvisionServer({
    fetchJson: serverFetch,
    ensureSession: ensureMerchantSession,
    ...args,
  })
}

/**
 * Idempotent wrapper: probes the server first, only provisions when
 * the restaurant isn't already `ready`. Kept local (not delegated) so the
 * injectable-dependency overrides used by serverProvisioningAutoHeal and
 * tests remain part of the signature.
 */
export async function provisionServerIfNeeded(args: {
  restaurantPubkey: string
  authorizationLabel: string
  timezone: string
  signAsRestaurant: RestaurantSigner
  mintServerAuth: () => Promise<{ bunker_url: string }>
  probeStatus?: (
    pubkey: string,
  ) => Promise<ServerRestaurantSnapshot | 'not_found'>
  ensureMerchantSessionFn?: (
    pubkey: string,
    signer: RestaurantSigner,
  ) => Promise<void>
  provisionServerFn?: typeof provisionServer
}): Promise<'already_registered' | 'provisioned'> {
  const authenticate = args.ensureMerchantSessionFn ?? ensureMerchantSession
  const probe = args.probeStatus ?? fetchServerRestaurantStatus
  const provision = args.provisionServerFn ?? provisionServer

  await authenticate(args.restaurantPubkey, args.signAsRestaurant)
  const snapshot = await probe(args.restaurantPubkey)
  if (
    snapshot !== 'not_found' &&
    snapshot.server_provisioning_status === 'ready'
  ) {
    return 'already_registered'
  }

  const serverAuth = await args.mintServerAuth()
  await provision({
    restaurantPubkey: args.restaurantPubkey,
    serverBunkerUrl: serverAuth.bunker_url,
    authorizationLabel: args.authorizationLabel,
    timezone: args.timezone,
    signAsRestaurant: args.signAsRestaurant,
  })
  return 'provisioned'
}

/** IANA timezone of the browser; delegates to the shared systemTimezone. */
export const browserTimezone = systemTimezone

/**
 * The restaurant's registered time zone, or null when the server has none (or
 * has no such restaurant). One silent re-authentication on 401.
 *
 * Read from the status snapshot rather than the takeout settings: it is the
 * same stored value, and the snapshot answers on every plan.
 *
 * **A zone this browser does not recognise reads as none.** Registration
 * stores the string it is sent, so the value can be anything; handing an
 * unknown zone to an event build throws and stops every time-based publish.
 * Read as none, the callers fall back and the next profile save corrects it.
 */
export async function fetchRegisteredTimezone(
  restaurantPubkey: string,
  signAsRestaurant: RestaurantSigner,
): Promise<string | null> {
  let snapshot: ServerRestaurantSnapshot | 'not_found'
  try {
    snapshot = await fetchServerRestaurantStatus(restaurantPubkey)
  } catch (err) {
    if (!(err instanceof ApiError) || err.code !== 'unauthorized') throw err
    await ensureMerchantSession(restaurantPubkey, signAsRestaurant)
    snapshot = await fetchServerRestaurantStatus(restaurantPubkey)
  }
  return snapshot === 'not_found' ? null : usableTimezone(snapshot.timezone)
}

/** `timezone` when it names an IANA zone this browser recognises, else null. */
export function usableTimezone(timezone: string | null | undefined): string | null {
  return timezone && isValidTimeZone(timezone) ? timezone : null
}

/**
 * Correct the restaurant's registered time zone — `PATCH
 * /api/restaurants/:restaurant_pubkey`. The server validates the zone and
 * answers with the updated snapshot. One silent re-authentication on 401.
 *
 * Changes the registered zone only: events, orders and stock already stored
 * are not rewritten (account#248).
 */
export async function correctRestaurantTimezone(
  restaurantPubkey: string,
  signAsRestaurant: RestaurantSigner,
  timezone: string,
): Promise<ServerRestaurantSnapshot> {
  const call = () =>
    serverFetch<ServerRestaurantSnapshot>(`/api/restaurants/${restaurantPubkey}`, {
      method: 'PATCH',
      body: JSON.stringify({ timezone }),
    })
  try {
    return await call()
  } catch (err) {
    if (!(err instanceof ApiError) || err.code !== 'unauthorized') throw err
    await ensureMerchantSession(restaurantPubkey, signAsRestaurant)
    return await call()
  }
}

/**
 * Remove the restaurant's registration from the server — the portal's delete
 * flow, after the relay events and before the Keycast team. The server
 * cancels and forgets any subscription, drops the registry row, and stops
 * using the bunker. One silent re-authentication on 401.
 */
export async function deregisterRestaurant(args: {
  restaurantPubkey: string
  signAsRestaurant: RestaurantSigner
}): Promise<void> {
  const call = () =>
    serverFetch<{ deleted: boolean }>(`/api/restaurants/${args.restaurantPubkey}`, {
      method: 'DELETE',
    })
  try {
    await call()
  } catch (err) {
    if (!(err instanceof ApiError) || err.code !== 'unauthorized') throw err
    await ensureMerchantSession(args.restaurantPubkey, args.signAsRestaurant)
    await call()
  }
}
